import { useState, useRef, useEffect, ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { RefreshProvider } from '@/contexts/RefreshContext';

// Helper for dynamic despia import
const triggerHaptic = () => {
  import('despia-native').then(({ default: despia }) => {
    despia('haptic://');
  }).catch(() => {});
};

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  disabled?: boolean;
}

export function PullToRefresh({ onRefresh, children, disabled = false }: PullToRefreshProps) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const maxPullDistance = 80;
  const refreshThreshold = 60;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    let startY = 0;
    let currentY = 0;
    let pulling = false;
    const pullThreshold = 5; // Minimum pull distance to start tracking

    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger if scrolled to top (check window since page scrolls at window level)
      if (window.scrollY <= 0) {
        startY = e.touches[0].clientY;
        touchStartY.current = startY;
        pulling = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isRefreshing) return;
      
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;

      // Only activate pull when genuinely pulling down past threshold
      if (diff > pullThreshold && window.scrollY <= 0) {
        pulling = true;
        e.preventDefault();
        const distance = Math.min((diff - pullThreshold) * 0.5, maxPullDistance);
        setPullDistance(distance);
        setIsPulling(true);

        // Haptic feedback at threshold
        if (distance >= refreshThreshold && pullDistance < refreshThreshold) {
          triggerHaptic();
        }
      } else if (diff < 0) {
        // User is scrolling up, stop pull tracking
        pulling = false;
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    const handleTouchEnd = async () => {
      if (!pulling) return;
      
      pulling = false;
      
      if (pullDistance >= refreshThreshold && !isRefreshing) {
        setIsRefreshing(true);
        triggerHaptic();
        
        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
          setIsPulling(false);
        }
      } else {
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance, isRefreshing, onRefresh, disabled]);

  const progress = Math.min(pullDistance / refreshThreshold, 1);
  const rotation = progress * 360;

  return (
    <div 
      ref={containerRef}
      className="h-full ios-scroll"
      style={{ 
        overscrollBehavior: 'none'
      }}
    >
      {/* Pull indicator */}
      <div 
        className="flex items-center justify-center transition-all"
        style={{
          height: isPulling || isRefreshing ? pullDistance : 0,
          opacity: isPulling || isRefreshing ? 1 : 0,
        }}
      >
        <RefreshCw 
          className={`w-5 h-5 text-primary transition-all ${isRefreshing ? 'animate-spin' : ''}`}
          style={{ 
            transform: isRefreshing ? 'rotate(360deg)' : `rotate(${rotation}deg)`,
            opacity: progress
          }}
        />
      </div>
      
      <RefreshProvider isRefreshing={isRefreshing}>
        {children}
      </RefreshProvider>
    </div>
  );
}
